import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { MaskedLines, MaskedLinesInView, Reveal } from "../components/Reveal";
import { IMG } from "../lib/images";

const CHAPTERS = [
  { n: "01", title: "Over a decade of expertise", body: "For more than ten years HR Direct Services has delivered staffing, recruiting and HR solutions for the evolving Southern California workforce — connecting great businesses with great people." },
  { n: "02", title: "Local hands, local speed", body: "With offices across the Inland Empire, Los Angeles and Orange County — plus New Jersey — we know the markets we serve. Proximity means we fill roles faster and show up when it matters." },
  { n: "03", title: "Staffing, payroll & PEO — handled", body: "Beyond placing talent, we run seamless payroll and PEO solutions: I-9s, benefits, compliance and onboarding managed end to end, so your team never carries the paperwork risk." },
  { n: "04", title: "Personalized, dedicated service", body: "You get a dedicated team — including on-site managers for high-volume accounts. We measure ourselves on how long our placements stay, not just how many we send." },
];

export default function About() {
  return (
    <div data-testid="about-page" className="pt-[72px]">
      <section className="mx-auto max-w-[1500px] px-6 md:px-10 pt-20 md:pt-28 pb-16">
        <p className="overline text-flame mb-6">About us</p>
        <h1 className="font-display uppercase tracking-tighter leading-[0.85] text-[13vw] lg:text-[8vw]">
          <MaskedLines lines={["A staffing", <span key="2" className="text-cobalt">partner, not a</span>, "vendor."]} delay={0.2} />
        </h1>
      </section>

      <section className="relative h-[60vh] overflow-hidden">
        <img src={IMG.warehouse2} alt="Warehouse team" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-cobalt/10" />
      </section>

      <section className="mx-auto max-w-[1500px] px-6 md:px-10 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4">
          <p className="overline text-cobalt sticky top-28">Who we are</p>
        </div>
        <div className="lg:col-span-8">
          <Reveal>
            <p className="font-display uppercase tracking-tight leading-[1.05] text-2xl md:text-4xl">
              Founded in 2014, HR Direct Services is a leading staffing agency in Southern California — headquartered in Ontario, with offices in Chino Hills, Commerce, Fontana, Moreno Valley, and Perth Amboy, NJ.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-muted-foreground text-lg mt-8 max-w-2xl">
              We specialize in HR staffing — temporary, temp-to-hire and direct hire solutions across industries like manufacturing, healthcare, logistics and clerical roles. Our payroll services help businesses streamline operations, while our job placement services empower candidates to find rewarding careers across Los Angeles, Orange County and the Inland Empire.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-[1500px] px-6 md:px-10 pb-24 md:pb-32 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4">
          <p className="overline text-cobalt sticky top-28">What sets us apart</p>
        </div>
        <div className="lg:col-span-8 space-y-16">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.n} delay={i * 0.05}>
              <div className="grid grid-cols-1 md:grid-cols-12 gap-6 border-t border-border pt-8">
                <span className="md:col-span-2 font-display text-6xl text-flame tracking-tighter leading-none">{c.n}</span>
                <div className="md:col-span-10">
                  <h2 className="font-display uppercase tracking-tight text-3xl md:text-4xl leading-[0.95]">{c.title}</h2>
                  <p className="text-muted-foreground text-lg mt-4 max-w-2xl">{c.body}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-y border-border bg-secondary/40">
        <div className="mx-auto max-w-[1500px] px-6 md:px-10 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-4">
            <p className="overline text-flame mb-4">Our mission</p>
            <h2 className="font-display uppercase tracking-tighter leading-[0.9] text-4xl md:text-5xl">Lasting<br />partnerships</h2>
          </div>
          <div className="lg:col-span-8">
            <Reveal>
              <p className="text-xl md:text-3xl leading-snug">
                To create lasting partnerships by delivering exceptional HR staffing and HR solutions. We understand the unique needs of employers and job seekers, offering personalized support to drive success — striving for <span className="text-cobalt">excellence, integrity and agility</span> in everything we do.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1500px] px-6 md:px-10 py-24 md:py-32">
        <p className="overline text-flame mb-10">Leadership</p>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-4">
            <div className="w-56 h-56 rounded-2xl overflow-hidden border-4 border-flame/30 bg-secondary">
              <img src="/team/gabriela.jpg" alt="Gabriela Mendoza, CEO & President" className="w-full h-full object-cover scale-[1.2] origin-bottom" data-testid="leadership-photo" />
            </div>
          </div>
          <div className="lg:col-span-8">
            <h2 className="font-display uppercase tracking-tighter leading-[0.9] text-5xl md:text-6xl">Gabriela Mendoza</h2>
            <p className="overline text-cobalt mt-4">CEO &amp; President · Ontario, CA</p>
            <p className="text-muted-foreground text-lg mt-6 max-w-xl">Leading HR Direct Services from our Ontario corporate headquarters, Gabriela drives the company's mission of building lasting partnerships across Southern California and beyond.</p>
          </div>
        </div>
      </section>

      <section className="bg-ink text-bone">
        <div className="mx-auto max-w-[1500px] px-6 md:px-10 py-24 md:py-32">
          <h2 className="font-display uppercase tracking-tighter leading-[0.9] text-4xl md:text-6xl max-w-3xl">
            <MaskedLinesInView lines={["One family of", "companies. One", "commitment to you."]} />
          </h2>
          <Reveal delay={0.2}>
            <p className="text-bone/60 text-lg mt-8 max-w-2xl">
              HR Direct Services Inc, The GHR Company, and The GHR Management Co. operate as one partner with a single promise: the right people, placed right, every time.
            </p>
            <Link to="/employers" className="inline-flex items-center gap-2 mt-8 bg-flame text-white px-6 py-3 rounded-full overline hover:bg-white hover:text-ink transition-colors duration-300">
              Work with us <ArrowUpRight size={14} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
